import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, Typography, TextField, Button, Alert, CircularProgress, Stack, Checkbox, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Box, MenuItem, Dialog, DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import featuredService from '../../services/featuredService';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import DialogHeader from '../Common/DialogHeader';

// Korai ajánlat kiküldése: kijelölöd a címzetteket, kiválasztod a sablont, megnézed az előnézetet,
// és csak ezután küldöd. A leiratkozottak nem jelölhetők. A sablonok az "Email sablonok" menüben
// szerkeszthetők (Korai ajánlat típus); az átvétel a backend EARLY_OFFER_ENABLED=true beállításával nyílik.
const AdminEarlyOfferPanel = () => {
  const [rows, setRows] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html, to, businessName }
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    featuredService.adminEarlyOfferRecipients()
      .then((response) => {
        const data = response?.data || response;
        setRows(data.recipients);
        setTemplates(data.templates);
        // Alapból az van kijelölve, aki nem iratkozott le és még nem vette át
        setSelected(new Set(data.recipients.filter(r => !r.unsubscribed && !r.claimed).map(r => r.id)));
        // Ha egyértelmű, melyik sablon: egyetlen sablon, vagy pontosan egy aktív
        const active = data.templates.filter(t => t.isActive);
        if (data.templates.length === 1) setTemplateId(data.templates[0].id);
        else if (active.length === 1) setTemplateId(active[0].id);
      })
      .catch((err) => setError(err?.message || 'Nem sikerült betölteni a címzetteket.'));
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (rows || []).filter(r => !q || r.businessName.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
  }, [rows, filter]);
  const selectable = visible.filter(r => !r.unsubscribed);
  const allSelected = selectable.length > 0 && selectable.every(r => selected.has(r.id));
  const currentTemplate = templates.find(t => t.id === templateId);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    selectable.forEach(r => (allSelected ? next.delete(r.id) : next.add(r.id)));
    return next;
  });

  // Előnézet egy konkrét címzett adataival (a levél neve, linkjei az övéi)
  const openPreview = async (row) => {
    if (!templateId) { setError('Előbb válassz sablont.'); return; }
    setPreviewBusy(true);
    setError('');
    try {
      const response = await featuredService.adminPreviewEarlyOffer(templateId, row.id);
      setPreview({ ...(response?.data || response), businessName: row.businessName });
    } catch (err) {
      setError(err?.message || 'Az előnézet nem készült el.');
    } finally {
      setPreviewBusy(false);
    }
  };
  const firstSelected = rows?.find(r => selected.has(r.id));

  const handleSend = async () => {
    const ids = [...selected];
    if (!window.confirm(`Biztosan kiküldöd a(z) „${currentTemplate.name}” sablont ${ids.length} szolgáltatónak?`)) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const response = await featuredService.adminSendEarlyOffer(ids, templateId, false);
      setResult(response?.data || response);
    } catch (err) {
      setError(err?.message || 'Hiba történt.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Korai ajánlat kiküldése</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          1. Válaszd ki a sablont, 2. jelöld a címzetteket, 3. nézd meg az előnézetet (a sor szemecske ikonjával, a címzett saját adataival), 4. küldd ki.
        </Typography>

        {!rows && !error && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {rows && templates.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nincs „Korai ajánlat” típusú sablon. Hozz létre egyet az Email sablonok menüben.
          </Alert>
        )}

        {rows && templates.length > 0 && (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { md: 'center' } }}>
              <TextField
                select size="small" label="Sablon" value={templateId}
                onChange={(e) => setTemplateId(e.target.value)} sx={{ minWidth: 280 }}
              >
                {templates.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}{t.isActive ? '' : ' (inaktív)'}</MenuItem>
                ))}
              </TextField>
              <TextField size="small" label="Keresés (név vagy email)" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </Stack>

            {currentTemplate && (
              <Typography variant="body2" sx={{ mb: 2 }}>Tárgy: <strong>{currentTemplate.subject}</strong></Typography>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
              <Typography variant="body2">Kijelölve: <strong>{selected.size}</strong> / {rows.length}</Typography>
              <Button variant="outlined" disabled={!templateId || !firstSelected || previewBusy} onClick={() => openPreview(firstSelected)}>
                Előnézet{firstSelected ? ` (${firstSelected.businessName})` : ''}
              </Button>
              <Button variant="contained" onClick={handleSend} disabled={busy || selected.size === 0 || !templateId}>
                {busy ? <CircularProgress size={22} color="inherit" /> : `Küldés a kijelölteknek (${selected.size})`}
              </Button>
            </Stack>

            <ResponsiveTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={allSelected} indeterminate={!allSelected && selectable.some(r => selected.has(r.id))} onChange={toggleAll} />
                    </TableCell>
                    <TableCell>Szolgáltató</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Regisztrált</TableCell>
                    <TableCell>Állapot</TableCell>
                    <TableCell align="right">Előnézet</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((r) => (
                    <TableRow key={r.id} hover selected={selected.has(r.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.has(r.id)} disabled={r.unsubscribed} onChange={() => toggle(r.id)} />
                      </TableCell>
                      <TableCell>{r.businessName}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{new Date(r.createdAt).toLocaleDateString('hu-HU')}</TableCell>
                      <TableCell>
                        {r.unsubscribed && <Chip size="small" color="warning" label="Leiratkozott" />}
                        {r.claimed && <Chip size="small" color="success" label="Már átvette" sx={{ ml: r.unsubscribed ? 1 : 0 }} />}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Előnézet ezzel a címzettel">
                          <span>
                            <IconButton size="small" disabled={!templateId || previewBusy} onClick={() => openPreview(r)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visible.length === 0 && (
                    <TableRow><TableCell colSpan={6}><Box sx={{ py: 2, textAlign: 'center' }}>Nincs találat.</Box></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </ResponsiveTableContainer>
          </>
        )}

        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Elküldve: {result.sent}, sikertelen: {result.failed}.
          </Alert>
        )}
      </CardContent>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogHeader icon={<Visibility />} title={`Előnézet: ${preview?.businessName || ''}`} onClose={() => setPreview(null)} />
        <DialogContent>
          {preview && (
            <>
              <Typography variant="body2">Címzett: <strong>{preview.to}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Tárgy: <strong>{preview.subject}</strong></Typography>
              <Box
                component="iframe"
                title="Email előnézet"
                srcDoc={preview.html}
                sandbox=""
                sx={{ width: '100%', height: '60vh', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Bezár</Button></DialogActions>
      </Dialog>
    </Card>
  );
};

export default AdminEarlyOfferPanel;
